// 26 ADM-102, the row "The round inspector" (10 §8; 07; 11) and ADM-104's Stage 2 part (decision record
// 0071). Owner, and support with a reason; this build asks every role for one. One read,
// ops.admin_round_inspect(round_id, reason): a finished round with its seed commit, its context, every
// hole, every stroke (the intent, the bands the golfer was shown beside the ledger of what happened,
// the reason and the flight block) and every decision, with the server's own re-run result. A round in
// play is never opened here (R-68): the server refuses it. Each open writes one audit.admin_actions row
// and, for an account's round, one audit.data_access_log row with the reason (ADM-010, ADM-011).
// "Re-run" is shown switched off, with the reason: the re-run is the round server's own
// (sim.verify_round), and no console command reaches it in this build (listed for the lead).
import type { SupabaseClient } from '@supabase/supabase-js';
import { useState } from 'react';
import { Breakable, Json } from '../components/Text';
import { adminLength, adminText } from '../lib/copy';
import { codeOf, messageFor } from '../lib/commands';
import { capitalWords, fixed, tidyNumbers, VALUE_BREAKS, words } from '../lib/tidy';

export interface InspectedRound {
  round: Record<string, unknown> & {
    id: string;
    status: string;
    seed_commit: string | null;
    seed_reveal: string | null;
    server_rerun_ok: boolean | null;
    server_rerun_at: string | null;
  };
  context: {
    golfer: unknown;
    conditions: unknown;
    routine: unknown;
    game_plan: unknown;
    stake_lines: unknown;
  };
  holes: {
    hole_no: number;
    par: number | null;
    strokes: number | null;
    putts: number | null;
    penalties: number | null;
    play_style: string | null;
  }[];
  strokes: {
    seq: number;
    hole_no: number;
    shot_no: number;
    club: string | null;
    shot_type: string | null;
    start_lie: string | null;
    end_lie: string | null;
    carry_m: number | null;
    total_m: number | null;
    penalty: number | null;
    holed: boolean | null;
    sg: number | null;
    card_only: boolean | null;
    intent: unknown;
    beliefs: unknown;
    ledger: unknown;
    reason: unknown;
    flight: unknown;
    choice_value: number | null;
  }[];
  decisions: {
    hole_no: number;
    shot_no: number | null;
    family: string;
    kind: string;
    options: unknown;
    caddie_advice: unknown;
    default_option_id: string | null;
    choice: unknown;
    source: string | null;
    seconds_charged: number | null;
    focus_charged: number | null;
    time_bank_left_s: number | null;
    focus_left: number | null;
    is_stop: boolean | null;
    state: string | null;
  }[];
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export async function inspectRound(
  sb: SupabaseClient,
  roundId: string,
  reason: string,
): Promise<{ data: InspectedRound } | { error: string }> {
  if (!UUID.test(roundId.trim())) return { error: 'round_not_found' };
  if (reason.trim() === '') return { error: 'reason_required' };
  try {
    const { data, error } = await sb
      .schema('ops')
      .rpc('admin_round_inspect', { p_round_id: roundId.trim(), p_reason: reason.trim() });
    if (error || !data) return { error: codeOf(error?.message) };
    return { data: data as InspectedRound };
  } catch {
    return { error: 'network' };
  }
}

const show = (v: unknown) =>
  v === null || v === undefined ? '—' : typeof v === 'string' ? v : JSON.stringify(v);

/** A stored time as "14 Oct 2026 at 08:00", or a dash. */
const when = (v: unknown) =>
  typeof v === 'string' && !Number.isNaN(Date.parse(v))
    ? adminText('rounds.when', { at: new Date(v) })
    : '—';

/** Strokes gained with its sign: "+0.68", "-1.15". */
const signed = (v: number | null) => (typeof v === 'number' && v > 0 ? '+' : '') + fixed(v, 2);

/** The book in force (ADM-104 point 5): from the Game plan's book_id; none before Stage 7. */
function bookLine(plan: unknown): string {
  const p = plan && typeof plan === 'object' ? (plan as Record<string, unknown>) : {};
  return typeof p.book_id === 'string'
    ? adminText('rounds.book_yes', { id: p.book_id, by: show(p.book_made_by) })
    : adminText('rounds.book_none');
}

function Facts({ r }: { r: InspectedRound['round'] }) {
  const rows: [string, unknown][] = [
    [adminText('rounds.f_status'), capitalWords(r.status)],
    [adminText('rounds.f_style'), capitalWords(r.play_style)],
    [adminText('rounds.f_kind'), capitalWords(r.kind)],
    [adminText('rounds.f_mode'), capitalWords(r.mode)],
    [adminText('rounds.f_engine'), r.sim_version],
    [adminText('rounds.f_holes'), r.holes_played],
    [adminText('rounds.f_gross'), r.gross],
    [adminText('rounds.f_net'), r.net],
    [adminText('rounds.f_points'), r.stableford_points],
    [adminText('rounds.f_caddie'), r.caddie_level_used],
    [adminText('rounds.f_started'), when(r.started_at)],
    [adminText('rounds.f_finished'), when(r.finished_at)],
  ];
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div key={k} className="fact">
          <dt>{k}</dt>
          <dd>{show(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The round's four energy figures (10 SIM-120) and its focus and time bank. */
function Meters({ r }: { r: InspectedRound['round'] }) {
  const rows: [string, unknown][] = [
    [adminText('rounds.e_start'), r.energy_start],
    [adminText('rounds.e_used'), r.energy_used],
    [adminText('rounds.e_food'), r.energy_food_back],
    [adminText('rounds.e_end'), r.energy_end],
    [adminText('rounds.focus_start'), r.focus_start],
    [adminText('rounds.focus_left'), r.focus_left],
    [adminText('rounds.bank_start'), r.time_bank_start_s],
    [adminText('rounds.bank_left'), r.time_bank_left_s],
  ];
  return (
    <dl className="facts">
      {rows.map(([k, v]) => (
        <div key={k} className="fact">
          <dt>{k}</dt>
          <dd>{show(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function Rerun({ r }: { r: InspectedRound['round'] }) {
  const at = r.server_rerun_at ? new Date(r.server_rerun_at) : null;
  const text =
    r.server_rerun_ok === null || r.server_rerun_ok === undefined
      ? adminText('rounds.rerun_none')
      : r.server_rerun_ok
        ? at
          ? adminText('rounds.rerun_ok', { at })
          : adminText('rounds.rerun_ok_undated')
        : at
          ? adminText('rounds.rerun_bad', { at })
          : adminText('rounds.rerun_bad_undated');
  // A line that wraps, not a chip: its words carry the result, the colour only repeats it.
  const cls =
    r.server_rerun_ok === true
      ? 'status-line ok'
      : r.server_rerun_ok === false
        ? 'status-line bad'
        : 'status-line none';
  return (
    <>
      <p className={cls}>{text}</p>
      <p className="actions">
        <button type="button" disabled aria-describedby="rerun-why">
          {adminText('rounds.rerun')}
        </button>
      </p>
      <p id="rerun-why" className="muted">
        {adminText('rounds.rerun_off')}
      </p>
    </>
  );
}

/** A choice longer than this, written on one line, is folded: a full shot's plan runs to 800. */
const SHORT_CHOICE = 60;

/** A decision's choice: on its own line when short, folded in full when long (DES-561 items 4, 5). */
function Choice({ v }: { v: unknown }) {
  const line = JSON.stringify(tidyNumbers(v)) ?? '—';
  if (line.length <= SHORT_CHOICE)
    return (
      <p>
        <span className="run-label">{adminText('rounds.choice')}</span>{' '}
        <code>
          <Breakable text={line} at={VALUE_BREAKS} />
        </code>
      </p>
    );
  const parts = Array.isArray(v)
    ? v.length
    : v && typeof v === 'object'
      ? Object.keys(v).length
      : 1;
  return (
    <details>
      <summary>{adminText('rounds.choice_full', { n: parts })}</summary>
      <Json v={v} />
    </details>
  );
}

function Stroke({ s }: { s: InspectedRound['strokes'][number] }) {
  return (
    <li className="card stroke">
      <details>
        <summary>
          <span className="stroke-head">
            {adminText('rounds.stroke_head', {
              hole: s.hole_no,
              shot: s.shot_no,
              club: words(s.club),
              from: words(s.start_lie),
              to: words(s.end_lie),
            })}
          </span>
          <span className="muted">
            {adminText('rounds.stroke_dist', { total: adminLength(s.total_m), sg: signed(s.sg) })}
          </span>
        </summary>
        <div className="two">
          <div>
            <p className="sub">{adminText('rounds.shown')}</p>
            <Json v={s.beliefs} />
          </div>
          <div>
            <p className="sub">{adminText('rounds.ledger')}</p>
            <Json v={s.ledger} />
          </div>
        </div>
        <p className="sub">{adminText('rounds.intent')}</p>
        <Json v={s.intent} />
        <p className="sub">{adminText('rounds.reason')}</p>
        <Json v={s.reason} />
        <p className="sub">{adminText('rounds.flight')}</p>
        <Json v={s.flight} />
      </details>
    </li>
  );
}

function Inspected({ d }: { d: InspectedRound }) {
  const r = d.round;
  return (
    <>
      <div className="panels sections">
        <section aria-labelledby="ri-s" className="block">
          <h3 id="ri-s">{adminText('rounds.summary_heading')}</h3>
          <p className="mono wrap">{r.id}</p>
          <Facts r={r} />
        </section>
        <section aria-labelledby="ri-seed" className="block">
          <h3 id="ri-seed">{adminText('rounds.seed_heading')}</h3>
          <dl className="facts">
            <div className="fact">
              <dt>{adminText('rounds.seed_commit')}</dt>
              <dd className="mono wrap">{show(r.seed_commit)}</dd>
            </div>
            <div className="fact">
              <dt>{adminText('rounds.seed_reveal')}</dt>
              <dd className="mono wrap">{show(r.seed_reveal)}</dd>
            </div>
            <div className="fact">
              <dt>{adminText('rounds.config_hash')}</dt>
              <dd className="mono wrap">{show(r.config_snapshot_hash)}</dd>
            </div>
          </dl>
          <Rerun r={r} />
        </section>
      </div>
      <div className="panels sections">
        <section aria-labelledby="ri-m" className="block">
          <h3 id="ri-m">{adminText('rounds.meters_heading')}</h3>
          <Meters r={r} />
        </section>
        <section aria-labelledby="ri-c" className="block">
          <h3 id="ri-c">{adminText('rounds.context_heading')}</h3>
          <p>{bookLine(d.context.game_plan)}</p>
          <details>
            <summary>{adminText('rounds.ctx_golfer')}</summary>
            <Json v={d.context.golfer} />
          </details>
          <details>
            <summary>{adminText('rounds.ctx_conditions')}</summary>
            <Json v={d.context.conditions} />
          </details>
          <details>
            <summary>{adminText('rounds.ctx_routine')}</summary>
            <Json v={d.context.routine} />
          </details>
          <details>
            <summary>{adminText('rounds.ctx_plan')}</summary>
            <Json v={d.context.game_plan} />
          </details>
          <details>
            <summary>{adminText('rounds.ctx_lines')}</summary>
            <Json v={d.context.stake_lines} />
          </details>
        </section>
      </div>
      <section aria-labelledby="ri-h" className="block">
        <h3 id="ri-h">{adminText('rounds.holes_heading', { n: d.holes.length })}</h3>
        {d.holes.length === 0 ? (
          <p className="muted">{adminText('rounds.holes_none')}</p>
        ) : (
          <div className="scroll">
            <table className="compact">
              <thead>
                <tr>
                  <th scope="col">Hole</th>
                  <th scope="col">Par</th>
                  <th scope="col">Strokes</th>
                  <th scope="col">Putts</th>
                  <th scope="col">Penalties</th>
                </tr>
              </thead>
              <tbody>
                {d.holes.map((h) => (
                  <tr key={h.hole_no}>
                    <th scope="row">{h.hole_no}</th>
                    <td>{show(h.par)}</td>
                    <td>{show(h.strokes)}</td>
                    <td>{show(h.putts)}</td>
                    <td>{show(h.penalties)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <section aria-labelledby="ri-st" className="block">
        <h3 id="ri-st">{adminText('rounds.strokes_heading', { n: d.strokes.length })}</h3>
        <p className="muted">{adminText('rounds.strokes_intro')}</p>
        <ul className="plain">
          {d.strokes.map((s) => (
            <Stroke key={s.seq} s={s} />
          ))}
        </ul>
      </section>
      <section aria-labelledby="ri-d" className="block">
        <h3 id="ri-d">{adminText('rounds.decisions_heading', { n: d.decisions.length })}</h3>
        <ul className="plain grid">
          {d.decisions.map((x, i) => (
            <li key={`${x.hole_no}-${x.shot_no ?? 0}-${i}`} className="card">
              <p className="title-row">
                <strong>
                  {x.shot_no
                    ? adminText('rounds.decision_head', {
                        hole: x.hole_no,
                        shot: x.shot_no,
                        family: words(x.family),
                        kind: words(x.kind),
                      })
                    : adminText('rounds.decision_head_between', {
                        hole: x.hole_no,
                        family: words(x.family),
                        kind: words(x.kind),
                      })}
                </strong>
              </p>
              <p className="muted">
                {adminText('rounds.decision_meta', {
                  who: words(x.source),
                  secs: x.seconds_charged ?? 0,
                  focus: x.focus_charged ?? 0,
                })}
              </p>
              <Choice v={x.choice} />
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

export function RoundInspectorPage({ sb }: { sb: SupabaseClient }) {
  const [roundId, setRoundId] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [data, setData] = useState<InspectedRound | null>(null);
  return (
    <>
      <h2>{adminText('rounds.heading')}</h2>
      <p className="muted">{adminText('rounds.intro')}</p>
      <form
        className="block"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setMsg('');
          const r = await inspectRound(sb, roundId, reason);
          setBusy(false);
          if ('error' in r) {
            setData(null);
            setMsg(messageFor({ ok: false, error: r.error }));
          } else setData(r.data);
        }}
      >
        <label htmlFor="ri-round">{adminText('rounds.round_label')}</label>
        <input
          id="ri-round"
          className="mono"
          value={roundId}
          onChange={(e) => setRoundId(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          required
        />
        <label htmlFor="ri-reason">{adminText('rounds.reason_label')}</label>
        <input id="ri-reason" value={reason} onChange={(e) => setReason(e.target.value)} required />
        <p>
          <button className="primary" type="submit" disabled={busy}>
            {adminText('rounds.open')}
          </button>
        </p>
        <p role="status" className={msg && !busy ? 'warn' : undefined}>
          {busy ? adminText('rounds.loading') : msg}
        </p>
      </form>
      {data && <Inspected d={data} />}
    </>
  );
}
